<template>
  <div class="error-shell">
    <div class="error-card">
      <component :is="icon" class="error-icon" :stroke-width="1.5" :style="{ color: 'var(--color-primary)' }" />
      <h1 class="error-title">{{ title }}</h1>
      <p class="error-body">{{ body }}</p>
      <div class="error-actions">
        <!-- 404: recargar no ayuda → "Volver al inicio" es la acción primaria (G-245 sigue:
             ambos botones manuales, nunca auto-reload) -->
        <AppButton
          :variant="isNotFound ? 'secondary' : 'primary'"
          :icon="RotateCw"
          :loading="reloading"
          data-testid="error-reload"
          @click="reload"
        >Recargar</AppButton>
        <AppButton
          :variant="isNotFound ? 'primary' : 'secondary'"
          :icon="Home"
          data-testid="error-home"
          @click="goHome"
        >Volver al inicio</AppButton>
      </div>
      <p v-if="showCode" class="error-code">Código de error: {{ error.statusCode }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { RotateCw, Home, WifiOff, AlertTriangle, SearchX } from 'lucide-vue-next'
import { isChunkLoadError } from '~/utils/isChunkLoadError'
import { hardResetAndReload } from '~/utils/swReset'
import { useOnlineStatus } from '~/composables/useOnlineStatus'
import AppButton from '~/components/ui/AppButton.vue'

// `error` lo inyecta Nuxt al renderizar este boundary global (createError / 404 / fatal).
const props = defineProps<{
  error: { statusCode?: number; statusMessage?: string; message?: string; name?: string }
}>()

const { online } = useOnlineStatus()

// Códigos típicos de fallo de red/gateway → tratamos como problema de conexión.
const NETWORK_CODES = new Set([0, 502, 503, 504])

const isConnectionIssue = computed(() =>
  !online.value
  || isChunkLoadError(props.error)
  || (props.error?.statusCode != null && NETWORK_CODES.has(props.error.statusCode)),
)

// 404 es un caso distinto de "algo salió mal": la página no existe, recargar no ayuda
// (Nielsen #9: el mensaje debe decir qué pasó y cómo salir).
const isNotFound = computed(() => !isConnectionIssue.value && props.error?.statusCode === 404)

const icon  = computed(() => (isConnectionIssue.value ? WifiOff : isNotFound.value ? SearchX : AlertTriangle))
const title = computed(() => (isConnectionIssue.value
  ? 'No se pudo cargar la página'
  : isNotFound.value ? 'Página no encontrada' : 'Algo salió mal'))
const body  = computed(() => (isConnectionIssue.value
  ? 'Revisa tu conexión a internet e intenta de nuevo.'
  : isNotFound.value
    ? 'La dirección no existe o fue movida. Revisa el enlace o vuelve al inicio.'
    : 'Ocurrió un error inesperado. Intenta recargar la página.'))

// El código solo confunde en fallos de conexión; mostrarlo solo en errores genéricos.
const showCode = computed(() => !isConnectionIssue.value && props.error?.statusCode != null)

// Recarga SIEMPRE manual (G-245). El click hace un HARD reset, no un
// `location.reload()` pelón: desregistra el SW y purga las cachés ANTES de recargar.
//
// Por qué (G-272): la causa más común de llegar a esta pantalla es un chunk
// `/_nuxt/[hash].js` que el deploy ya borró del bucket, pedido por un shell viejo
// que el Service Worker sigue sirviendo desde `healdesk-pages`/`healdesk-assets`.
// Un reload normal NO desregistra el SW → mismo shell viejo → mismo chunk borrado
// → esta misma pantalla. El botón "Recargar" no recuperaba nada: era un bucle.
//
// Aquí el hard reset es siempre seguro: si `error.vue` está montado, la app no
// llegó a renderizar, así que no hay trabajo sin guardar que destruir. Y
// `purgeServiceWorkerAndCaches` no toca `localStorage`, así que no desloguea.
const reloading = ref(false)
async function reload() {
  if (reloading.value) return
  reloading.value = true
  await hardResetAndReload()
}
function goHome() {
  clearError({ redirect: '/' })
}
</script>

<style scoped>
.error-shell {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--color-bg, #f8fafc);
}
.error-card {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  padding: 32px 24px;
  border-radius: 16px;
  background: var(--color-surface, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}
.error-icon {
  width: 48px;
  height: 48px;
}
.error-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text, #0f172a);
  margin: 0;
}
.error-body {
  font-size: 14px;
  color: var(--color-text-muted, #64748b);
  margin: 0;
  line-height: 1.5;
}
.error-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 8px;
}
.error-code {
  font-size: 11px;
  color: var(--color-text-muted, #94a3b8);
  margin: 4px 0 0;
}
</style>
